import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { useAgencyResetPasswordMutation } from '../slices/agencyAuthApiSlice';
import { apiSlice } from '../slices/apiSlice';

// Mirrors the same password rule the backend enforces (resetPasswordSchema in
// backend/utils/joi_schemas/resetPassword.js): at least 8 characters, with an
// uppercase letter, a lowercase letter, a digit and a special character. This
// is a client-side hint only — the server is what actually enforces it.
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?!.*\s).{8,}$/;

// Final step of the agency "forgot password" flow. Reached only after the
// email + 6-digit reset code have both been verified on LoginScreen — the
// email itself arrives via router state (never persisted), and the actual
// authority to reset is the httpOnly `pset` cookie the backend already set;
// this screen never sees or sends a reset token.
const ResetPasswordScreen = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [resetPassword, { isLoading }] = useAgencyResetPasswordMutation();

  useEffect(() => {
    const emailFromState = location.state?.email;
    if (!emailFromState) {
      toast.error('Reset session not found. Please request a new code.');
      navigate('/login');
      return;
    }
    setEmail(emailFromState);
  }, [location.state, navigate]);

  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const passwordValid = PASSWORD_REGEX.test(password);

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Email is missing');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (!passwordValid) {
      toast.error('Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number and a special character (!@#$%^&*)');
      return;
    }

    try {
      // resetToken is NOT sent — the backend reads it from the httpOnly
      // `pset` cookie set when the reset code was verified.
      await resetPassword({ email, password }).unwrap();

      dispatch(apiSlice.util.resetApiState());
      toast.success('Password reset successfully. Please sign in.');
      navigate('/login');
    } catch (err) {
      toast.error(err?.data?.error || err?.data?.message || 'Could not reset password');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 420 }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
          Set a New Password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose a new password for your agency account.
        </Typography>

        <Box component="form" onSubmit={submitHandler} noValidate>
          <TextField
            label="Email"
            type="email"
            fullWidth
            required
            margin="normal"
            value={email}
            InputProps={{ readOnly: true }}
          />

          <TextField
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            required
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            error={password.length > 0 && !passwordValid}
            helperText="At least 8 characters, with an uppercase letter, a lowercase letter, a number and a special character (!@#$%^&*)"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Confirm New Password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            required
            margin="normal"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={confirmPassword.length > 0 && !passwordsMatch}
            helperText={confirmPassword.length > 0 && !passwordsMatch ? 'Passwords do not match' : ' '}
          />

          {password.length > 0 && confirmPassword.length > 0 && passwordsMatch && (
            <Alert severity="success" sx={{ mt: 1 }}>
              Passwords match
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            sx={{ mt: 3 }}
            disabled={isLoading || !passwordsMatch || !passwordValid}
          >
            {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Reset Password'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ResetPasswordScreen;
